import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  LeadRevealType,
} from './types';

const getDefaultTabs = () => ({
  leadType: LeadFinderPrimaryTab.COMPANY,
  subTab: LeadFinderSecondaryTab.TOTAL,
});

const isPrimaryTabValue = (
  value?: string,
): value is LeadFinderPrimaryTab =>
  !!value &&
  Object.values(LeadFinderPrimaryTab).includes(
    value as LeadFinderPrimaryTab,
  );

const isSecondaryTabValue = (
  value?: string,
): value is LeadFinderSecondaryTab =>
  !!value &&
  Object.values(LeadFinderSecondaryTab).includes(
    value as LeadFinderSecondaryTab,
  );

const resolveInitialTabs = () => {
  if (typeof window === 'undefined' || !window.location?.pathname) {
    return getDefaultTabs();
  }

  const segments = window.location.pathname
    .split('/')
    .filter((segment) => segment);
  const leadsIndex = segments.findIndex((segment) => segment === 'leads');

  if (leadsIndex === -1) {
    return getDefaultTabs();
  }

  const primaryCandidate = segments[leadsIndex + 1];
  const secondaryCandidate = segments[leadsIndex + 2];

  return {
    leadType: isPrimaryTabValue(primaryCandidate)
      ? primaryCandidate
      : LeadFinderPrimaryTab.COMPANY,
    subTab: isSecondaryTabValue(secondaryCandidate)
      ? secondaryCandidate
      : LeadFinderSecondaryTab.TOTAL,
  };
};

const { leadType: initialLeadType, subTab: initialSubTab } =
  resolveInitialTabs();

export interface LeadFinderV2State {
  isSidebarExpanded: boolean;
  isLoading: boolean;
  showAiSearchPopup: boolean;
  filterValue: string;
  aiSearchInputValue: string;
  aiSeachPopupSubmitted: boolean;
  isAiSearchInProgress: boolean;
  activeLeadType: LeadFinderPrimaryTab;
  activeSubTab: LeadFinderSecondaryTab;
  aiChat: {
    showPanel: boolean;
    activeConversationId: string | null;
    pendingQuery: string | null;
    initialView: 'chat' | 'history';
  };
  bulkPeopleSearch: {
    isPending: boolean;
    filters: string;
    isBulkModeActive: boolean;
  };
  selection: {
    selectedLeads: number[];
    isSelectedAllLeads: boolean;
    deSelectedLeads: number[];
    selectedCompanies: number[];
    isSelectedAllCompanies: boolean;
    peopleMaxLeadsLimit: number | null;
    companyMaxLeadsLimit: number | null;
  };
  modals: {
    showCreditNotAvailableModal: boolean;
    creditCheckRevealType: LeadRevealType | null;
    showSelectedMoreThanLimitModal: boolean;
    showAddToSequenceModal: boolean;
    selectedLeadIdForSequence: number | null;
    showBulkRevealTagModal: boolean;
    showRemoveFromListModal: boolean;
    showGlobalRestrictionModal: boolean;
  };
}

const initialState: LeadFinderV2State = {
  isSidebarExpanded: true,
  isLoading: false,
  showAiSearchPopup: false,
  filterValue: '',
  aiSearchInputValue: '',
  aiSeachPopupSubmitted: false,
  isAiSearchInProgress: false,
  activeLeadType: initialLeadType,
  activeSubTab: initialSubTab,
  aiChat: {
    showPanel: false,
    activeConversationId: null,
    pendingQuery: null,
    initialView: 'chat',
  },
  bulkPeopleSearch: { isPending: false, filters: '', isBulkModeActive: false },
  selection: {
    selectedLeads: [],
    isSelectedAllLeads: false,
    deSelectedLeads: [],
    selectedCompanies: [],
    isSelectedAllCompanies: false,
    peopleMaxLeadsLimit: null,
    companyMaxLeadsLimit: null,
  },
  modals: {
    showCreditNotAvailableModal: false,
    creditCheckRevealType: null,
    showSelectedMoreThanLimitModal: false,
    showAddToSequenceModal: false,
    selectedLeadIdForSequence: null,
    showBulkRevealTagModal: false,
    showRemoveFromListModal: false,
    showGlobalRestrictionModal: false,
  },
};

const leadFinderV2Slice = createSlice({
  name: 'leadFinderV2',
  initialState,
  reducers: {
    setSidebarExpanded(state, action: PayloadAction<boolean>) {
      state.isSidebarExpanded = action.payload;
    },
    setLeadFinderV2Loading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setShowAiSearchPopup(state, action: PayloadAction<boolean>) {
      state.showAiSearchPopup = action.payload;
    },
    setFilterValue(state, action: PayloadAction<string>) {
      state.filterValue = action.payload;
    },
    setAiSearchPopupInputValue(state, action: PayloadAction<string>) {
      state.aiSearchInputValue = action.payload;
    },
    setAiSearchPopupSubmitted(state, action: PayloadAction<boolean>) {
      state.aiSeachPopupSubmitted = action.payload;
    },
    setIsAiSearchInProgress(state, action: PayloadAction<boolean>) {
      state.isAiSearchInProgress = action.payload;
    },
    setActiveLeadType(
      state,
      action: PayloadAction<LeadFinderPrimaryTab>,
    ) {
      state.activeLeadType = action.payload;
    },
    setActiveSubTab(
      state,
      action: PayloadAction<LeadFinderSecondaryTab>,
    ) {
      state.activeSubTab = action.payload;
    },
    setBulkPeopleSearch(
      state,
      action: PayloadAction<{ filters: string }>,
    ) {
      state.bulkPeopleSearch.isPending = true;
      state.bulkPeopleSearch.filters = action.payload.filters;
    },
    clearBulkPeopleSearch(state) {
      state.bulkPeopleSearch.isPending = false;
      state.bulkPeopleSearch.filters = '';
    },
    setBulkPeopleModeActive(state, action: PayloadAction<boolean>) {
      state.bulkPeopleSearch.isBulkModeActive = action.payload;
    },
    setSelectedLeads(state, action: PayloadAction<number[]>) {
      state.selection.selectedLeads = action.payload;
    },
    setIsSelectedAllLeads(state, action: PayloadAction<boolean>) {
      state.selection.isSelectedAllLeads = action.payload;
      // Reset any custom limit when select-all is turned off so re-selecting
      // starts fresh at the default cap.
      if (!action.payload) {
        state.selection.peopleMaxLeadsLimit = null;
      }
    },
    setDeSelectedLeads(state, action: PayloadAction<number[]>) {
      state.selection.deSelectedLeads = action.payload;
    },
    setSelectedCompanies(state, action: PayloadAction<number[]>) {
      state.selection.selectedCompanies = action.payload;
    },
    setIsSelectedAllCompanies(state, action: PayloadAction<boolean>) {
      state.selection.isSelectedAllCompanies = action.payload;
      if (!action.payload) {
        state.selection.companyMaxLeadsLimit = null;
      }
    },
    setPeopleMaxLeadsLimit(state, action: PayloadAction<number | null>) {
      state.selection.peopleMaxLeadsLimit = action.payload;
    },
    setCompanyMaxLeadsLimit(state, action: PayloadAction<number | null>) {
      state.selection.companyMaxLeadsLimit = action.payload;
    },
    clearPeopleSelection(state) {
      state.selection.selectedLeads = [];
      state.selection.isSelectedAllLeads = false;
      state.selection.deSelectedLeads = [];
      state.selection.peopleMaxLeadsLimit = null;
    },
    clearCompanySelection(state) {
      state.selection.selectedCompanies = [];
      state.selection.isSelectedAllCompanies = false;
      state.selection.companyMaxLeadsLimit = null;
    },
    openCreditNotAvailableModal(state, action: PayloadAction<LeadRevealType>) {
      state.modals.showCreditNotAvailableModal = true;
      state.modals.creditCheckRevealType = action.payload;
    },
    closeCreditNotAvailableModal(state) {
      state.modals.showCreditNotAvailableModal = false;
      state.modals.creditCheckRevealType = null;
    },
    setShowSelectedMoreThanLimitModal(state, action: PayloadAction<boolean>) {
      state.modals.showSelectedMoreThanLimitModal = action.payload;
    },
    openAddToSequenceModal(state, action: PayloadAction<number | null>) {
      state.modals.showAddToSequenceModal = true;
      state.modals.selectedLeadIdForSequence = action.payload;
    },
    closeAddToSequenceModal(state) {
      state.modals.showAddToSequenceModal = false;
      state.modals.selectedLeadIdForSequence = null;
    },
    setShowBulkRevealTagModal(state, action: PayloadAction<boolean>) {
      state.modals.showBulkRevealTagModal = action.payload;
    },
    openAiChatPanel(state, action: PayloadAction<string | undefined>) {
      state.aiChat.showPanel = true;
      state.aiChat.pendingQuery = action.payload ?? null;
      state.aiChat.initialView = 'chat';
    },
    openAiChatHistory(state) {
      state.aiChat.showPanel = true;
      state.aiChat.pendingQuery = null;
      state.aiChat.initialView = 'history';
    },
    closeAiChatPanel(state) {
      state.aiChat.showPanel = false;
      state.aiChat.pendingQuery = null;
    },
    setActiveChatConversationId(state, action: PayloadAction<string | null>) {
      state.aiChat.activeConversationId = action.payload;
    },
    clearAiChatPendingQuery(state) {
      state.aiChat.pendingQuery = null;
    },
    resetAiChatInitialView(state) {
      state.aiChat.initialView = 'chat';
    },
    setShowRemoveFromListModal(state, action: PayloadAction<boolean>) {
      state.modals.showRemoveFromListModal = action.payload;
    },
    setShowGlobalRestrictionModal(state, action: PayloadAction<boolean>) {
      state.modals.showGlobalRestrictionModal = action.payload;
    },
  },
});

export const {
  setSidebarExpanded,
  setLeadFinderV2Loading,
  setShowAiSearchPopup,
  setFilterValue,
  setAiSearchPopupInputValue,
  setAiSearchPopupSubmitted,
  setIsAiSearchInProgress,
  setActiveLeadType,
  setActiveSubTab,
  openAiChatPanel,
  openAiChatHistory,
  closeAiChatPanel,
  setActiveChatConversationId,
  clearAiChatPendingQuery,
  resetAiChatInitialView,
  setBulkPeopleSearch,
  clearBulkPeopleSearch,
  setBulkPeopleModeActive,
  setSelectedLeads,
  setIsSelectedAllLeads,
  setDeSelectedLeads,
  setSelectedCompanies,
  setIsSelectedAllCompanies,
  setPeopleMaxLeadsLimit,
  setCompanyMaxLeadsLimit,
  clearPeopleSelection,
  clearCompanySelection,
  openCreditNotAvailableModal,
  closeCreditNotAvailableModal,
  setShowSelectedMoreThanLimitModal,
  openAddToSequenceModal,
  closeAddToSequenceModal,
  setShowBulkRevealTagModal,
  setShowRemoveFromListModal,
  setShowGlobalRestrictionModal,
} = leadFinderV2Slice.actions;

export default leadFinderV2Slice.reducer;
